import type {
  Asset, AudioProps, Clip, ClipKind, ColorAdjust, Project, SubStyle, TextProps, Track, TrackKind, Transform, VaideoOrigin,
} from '../types'

export function uid(len = 10): string {
  const a = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let s = ''
  const r = crypto.getRandomValues(new Uint8Array(len))
  for (const b of r) s += a[b % a.length]
  return s
}

export const ASPECTS = [
  { key: '16:9', label: '16:9', hint: 'YouTube', width: 1920, height: 1080 },
  { key: '9:16', label: '9:16', hint: 'TikTok · Reels · Shorts', width: 1080, height: 1920 },
  { key: '1:1', label: '1:1', hint: 'Instagram', width: 1080, height: 1080 },
  { key: '4:5', label: '4:5', hint: 'Feed vertical', width: 1080, height: 1350 },
] as const

export function aspectKey(w: number, h: number): string {
  const found = ASPECTS.find((a) => Math.abs(a.width / a.height - w / h) < 0.01)
  return found ? found.key : `${w}×${h}`
}

export const defaultTransform = (): Transform => ({ x: 0, y: 0, scale: 1, rotation: 0, opacity: 1 })
export const defaultAudio = (): AudioProps => ({ volume: 1, muted: false, fadeInSec: 0, fadeOutSec: 0 })
export const defaultColor = (): ColorAdjust => ({
  brightness: 0, contrast: 0, saturation: 0, hue: 0, temperature: 0, exposure: 0,
})
export const defaultText = (text = 'Texto'): TextProps => ({
  text, fontSize: 96, color: '#ffffff', backgroundColor: 'transparent', bold: true, italic: false, align: 'center',
})
export const defaultSubStyle = (): SubStyle => ({
  fontSize: 64, fontWeight: 800, color: '#ffffff', backgroundColor: 'transparent',
  shadow: true, outline: true, outlineColor: '#000000', position: 'bottom',
})

export function newTrack(kind: TrackKind, name: string): Track {
  return { id: uid(8), kind, name, muted: false, locked: false, visible: true }
}

export function newProject(width = 1920, height = 1080): Project {
  const now = Date.now()
  return {
    schema: 2,
    id: uid(16),
    name: 'Proyecto sin título',
    createdAt: now,
    modifiedAt: now,
    settings: { width, height, fps: 30, backgroundColor: '#000000', sampleRate: 48000 },
    tracks: [newTrack('video', 'Principal'), newTrack('audio', 'Audio 1')],
    clips: [],
    assets: [],
    durationSec: 0,
    subtitleStyle: defaultSubStyle(),
  }
}

export function clipEnd(c: Clip): number {
  return c.startSec + c.durationSec
}

export function computeDuration(p: Project): number {
  return p.clips.reduce((m, c) => Math.max(m, clipEnd(c)), 0)
}

export function isMedia(c: Clip): boolean {
  return c.kind === 'video' || c.kind === 'audio' || c.kind === 'image'
}

export function isVisual(c: Clip): boolean {
  return c.kind !== 'audio'
}

export function hasTimedSource(c: Clip): boolean {
  return c.kind === 'video' || c.kind === 'audio'
}

export function trackKindFor(kind: ClipKind): TrackKind {
  return kind === 'audio' ? 'audio' : 'video'
}

export function assetOf(p: Project, c: Clip): Asset | undefined {
  return c.assetId ? p.assets.find((a) => a.id === c.assetId) : undefined
}

/** Max timeline length for a clip given its source (Infinity for stills/text). */
export function maxClipDuration(p: Project, c: Clip): number {
  if (!hasTimedSource(c)) return Infinity
  const a = assetOf(p, c)
  if (!a) return Infinity
  return Math.max(0.05, (a.info.durationSec - c.sourceInSec) / c.speed)
}

export function clipFromAsset(asset: Asset, trackId: string, startSec: number): Clip {
  const kind: ClipKind = asset.info.kind
  const dur = kind === 'image' ? 5 : Math.max(0.1, asset.info.durationSec)
  return {
    id: uid(10),
    trackId,
    kind,
    assetId: asset.id,
    startSec,
    durationSec: dur,
    sourceInSec: 0,
    sourceOutSec: dur,
    speed: 1,
    transform: defaultTransform(),
    audio: defaultAudio(),
    color: defaultColor(),
  }
}

export function textClip(trackId: string, startSec: number, text: Partial<TextProps>, durationSec = 3): Clip {
  return {
    id: uid(10), trackId, kind: 'text', startSec, durationSec,
    sourceInSec: 0, sourceOutSec: durationSec, speed: 1,
    transform: defaultTransform(),
    audio: { ...defaultAudio(), muted: true },
    color: defaultColor(),
    text: { ...defaultText(), ...text },
  }
}

export function subtitleClip(trackId: string, startSec: number, durationSec: number, text: string): Clip {
  return {
    id: uid(10), trackId, kind: 'subtitle', startSec, durationSec,
    sourceInSec: 0, sourceOutSec: durationSec, speed: 1,
    transform: defaultTransform(),
    audio: { ...defaultAudio(), muted: true },
    color: defaultColor(),
    rawText: text,
    positionMode: 'preset',
  }
}

/** Keep track order canonical: visual tracks first (top→bottom), then audio. */
export function sortTracks(tracks: Track[]): Track[] {
  return [...tracks.filter((t) => t.kind === 'video'), ...tracks.filter((t) => t.kind === 'audio')]
}

/**
 * Accept anything that looks like a project (v2 or legacy editor save) and
 * return a valid v2 Project. Missing fields get defaults.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizeProject(raw: any): Project {
  const base = newProject()
  if (!raw || typeof raw !== 'object') return base
  const settings = { ...base.settings, ...(raw.settings ?? {}) }
  // Legacy tracks carry an `index` (higher = front); v2 uses array order.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let tracksRaw: any[] = Array.isArray(raw.tracks) ? [...raw.tracks] : []
  if (tracksRaw.some((t) => typeof t.index === 'number')) {
    tracksRaw.sort((a, b) => (b.index ?? 0) - (a.index ?? 0))
  }
  let tracks: Track[] = tracksRaw.map((t) => ({
    id: String(t.id ?? uid(8)),
    kind: t.kind === 'audio' ? 'audio' : 'video',
    name: String(t.name ?? (t.kind === 'audio' ? 'Audio' : 'Vídeo')),
    muted: !!t.muted,
    locked: !!t.locked,
    visible: t.visible !== false,
  }))
  if (!tracks.some((t) => t.kind === 'video')) tracks.unshift(newTrack('video', 'Principal'))
  if (!tracks.some((t) => t.kind === 'audio')) tracks.push(newTrack('audio', 'Audio 1'))
  tracks = sortTracks(tracks)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const assets: Asset[] = (Array.isArray(raw.assets) ? raw.assets : []).filter((a: any) => a && a.info).map((a: any) => ({
    id: String(a.id ?? String(a.info.path).split('/').pop()),
    name: String(a.name ?? a.originalFilename ?? 'Archivo'),
    info: a.info,
    source: a.source,
  }))

  const trackIds = new Set(tracks.map((t) => t.id))
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clips: Clip[] = (Array.isArray(raw.clips) ? raw.clips : []).map((c: any) => {
    const kind: ClipKind = ['video', 'audio', 'image', 'text', 'subtitle'].includes(c.kind) ? c.kind : 'video'
    let trackId = String(c.trackId ?? '')
    const wanted = trackKindFor(kind)
    const tr = tracks.find((t) => t.id === trackId)
    if (!trackIds.has(trackId) || !tr || tr.kind !== wanted) {
      trackId = tracks.find((t) => t.kind === wanted)!.id
    }
    const speed = Number(c.speed) > 0 ? Number(c.speed) : 1
    const durationSec = Math.max(0.05, Number(c.durationSec ?? (c.sourceOutSec - c.sourceInSec) / speed) || 3)
    const sourceInSec = Math.max(0, Number(c.sourceInSec) || 0)
    const clip: Clip = {
      id: String(c.id ?? uid(10)),
      trackId,
      kind,
      assetId: c.assetId ?? (c.sourcePath ? String(c.sourcePath).split('/').pop() : undefined),
      startSec: Math.max(0, Number(c.startSec) || 0),
      durationSec,
      sourceInSec,
      sourceOutSec: Number(c.sourceOutSec) || sourceInSec + durationSec * speed,
      speed,
      transform: { ...defaultTransform(), ...(c.transform ?? {}) },
      audio: { ...defaultAudio(), ...(c.audio ?? {}) },
      color: { ...defaultColor(), ...(c.color ?? {}) },
    }
    if (kind === 'text') clip.text = { ...defaultText(), ...(c.text ?? {}) }
    if (kind === 'subtitle') {
      clip.rawText = String(c.rawText ?? (Array.isArray(c.words) ? c.words.map((w: { text: string }) => w.text).join(' ') : ''))
      if (c.subStyle) clip.subStyle = { ...defaultSubStyle(), ...c.subStyle }
      clip.positionMode = c.positionMode === 'free' ? 'free' : 'preset'
    }
    return clip
  })

  const p: Project = {
    schema: 2,
    id: String(raw.id ?? base.id),
    name: String(raw.name ?? base.name),
    createdAt: Number(raw.createdAt) || base.createdAt,
    modifiedAt: Number(raw.modifiedAt) || base.modifiedAt,
    settings,
    tracks,
    clips,
    assets,
    durationSec: 0,
    subtitleStyle: { ...defaultSubStyle(), ...(raw.subtitleStyle ?? {}) },
    vaideoUid: raw.vaideoUid,
    origin: normalizeOrigin(raw.origin),
  }
  p.durationSec = computeDuration(p)
  return p
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeOrigin(o: any): VaideoOrigin | undefined {
  if (!o || typeof o !== 'object' || !(Number(o.width) > 0) || !(Number(o.height) > 0)) return undefined
  return {
    uid: String(o.uid ?? ''),
    aspectRatio: String(o.aspectRatio ?? aspectKey(Number(o.width), Number(o.height))),
    width: Number(o.width),
    height: Number(o.height),
    fps: Number(o.fps) > 0 ? Number(o.fps) : 30,
    language: o.language ?? null,
    durationSec: Number(o.durationSec) || null,
  }
}

/** True when the canvas keeps the aspect ratio the Vaideo video was created with. */
export function matchesOrigin(p: Project, w = p.settings.width, h = p.settings.height): boolean {
  if (!p.origin) return true
  return Math.abs(w / h - p.origin.width / p.origin.height) < 0.01
}

/**
 * Ask before moving an imported Vaideo video away from its original format —
 * it would be letterboxed (black bars) in the preview and in the export.
 */
export function confirmAspectChange(p: Project, w: number, h: number): boolean {
  if (!p.origin || matchesOrigin(p, w, h) || !matchesOrigin(p)) return true
  return window.confirm(
    `Este vídeo se creó en ${p.origin.aspectRatio} (${p.origin.width}×${p.origin.height}). ` +
    `Si cambias el lienzo a ${aspectKey(w, h)} se verá con bandas negras o recortado. ¿Cambiar el formato igualmente?`,
  )
}

export function formatTime(sec: number, fps = 30): string {
  if (!isFinite(sec) || sec < 0) sec = 0
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  const f = Math.floor((sec - Math.floor(sec)) * fps + 1e-6)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}:${String(f).padStart(2, '0')}`
}

export function formatShort(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}
